import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, SxProps, Theme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QcsButton } from '../common/basic/QcsButton';
import { copyToClipboard } from '../../utils/clipboard';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';

interface CopyButtonProps {
  text: string;
  //Without one, just the icon is displayed.
  label?: string;
  onCopy?: () => void;
  sx?: SxProps<Theme>;
}

export const CopyButton: FC<CopyButtonProps> = ({ text, label, onCopy, sx }) => {
  const { t } = useTranslation();
  const { enqueueSuccessSnackbar } = useAppSnackbar();

  const handleCopy = async () => {
    await copyToClipboard(text);
    enqueueSuccessSnackbar(t('common.copySuccess'));
    onCopy?.();
  };

  if (label) {
    return (
      <QcsButton
        variant="text"
        startIcon={<ContentCopyIcon />}
        onClick={handleCopy}
        sx={[{ textTransform: 'none' }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {label}
      </QcsButton>
    );
  }

  return (
    <QcsButton onClick={handleCopy} sx={[{ minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <ContentCopyIcon fontSize="small" />
    </QcsButton>
  );
};

/** Monospace text (commands, a key) with a copy button. */
export const CopyableBlock: FC<{ text: string; onCopy?: () => void }> = ({ text, onCopy }) => (
  <Box sx={{ position: 'relative', backgroundColor: 'grey.100', borderRadius: 1, p: 2, pr: 7 }}>
    <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {text}
    </Box>
    <CopyButton text={text} onCopy={onCopy} sx={{ position: 'absolute', top: 4, right: 4 }} />
  </Box>
);
